import { ChangeEvent, useEffect, useState } from "react";
import api from "../../services/api";
import { useToast } from "../../hooks/toast";
import { useAuth } from "../../hooks/auth";
import { useHistory } from "react-router-dom";
import {
  AttachFileButton,
  Container,
  ErrorMessageContainer,
  TemplatesTable,
} from "./styles";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import { MdOutlineAttachment } from "react-icons/md";
import refreshToken from "../../utils/refreshToken";
import TemplateCard from "./components/TemplateCard";
import TemplatesTableSkeleton from "./components/TemplatesTableSkeleton";
import TemplatesFallback from "./components/TemplatesFallback";
import ModalRemoveTemplate from "./components/ModalRemoveTemplate";

type ContractField = {
  [key: string]: string;
};

type ContractTemplate = {
  id: number;
  filename: string;
  google_drive_id: string;
  created_by: number;
  deleted_at: Date | null;
  fields: ContractField[];
};

function extractFileExtension(filename: string) {
  const extension = filename.split(".")[1];
  return extension;
}

const ShowContractTemplates = () => {
  const history = useHistory();
  const { access_token, user, dateToExpires } = useAuth();
  const { addToast } = useToast();
  const [templates, setTemplates] = useState<ContractTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [uploadingTemplates, setUploadingTemplates] = useState(false);

  const [removeTemplate, setRemoveTemplate] = useState({
    openModal: false,
    isRemoving: false,
    templateId: 0,
    filename: "",
  });

  const [loadApiError, setLoadApiError] = useState("");

  async function loadApi() {
    try {
      const token = await refreshToken(dateToExpires, access_token);
      api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;

      const { data } = await api.get("/api/adm/contracts/templates");
      const fetchedTemplates = data.data;

      setTemplates(fetchedTemplates);

      setLoadingTemplates(false);
    } catch (err: any) {
      if (err?.response?.status === 403) {
        setLoadApiError(err?.response?.data?.message);

        setLoadingTemplates(true);
      }

      if (!!err.response?.data.message) {
        addToast({
          title: err.response.data.message,
          type: "error",
        });
      }

      setLoadApiError(err.response.data.message);

      setLoadingTemplates(true);
    }
  }
  useEffect(() => {
    if (!!user) {
      loadApi();
    } else {
      history.push("/");
    }

    // eslint-disable-next-line
  }, [dateToExpires, access_token, user, history]);

  async function onFileUpload(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (file) {
      const body = new FormData();
      body.append("template", file);

      try {
        setUploadingTemplates(true);
        const response = await api.post(
          "/api/adm/contracts/templates/upload",
          body
        );
        addToast({ title: "Modelo criado com sucesso!", type: "success" });
        if (response.data.success)
          setTemplates((prevTemplates) => [
            ...prevTemplates,
            response.data.data,
          ]);
      } catch (err: any) {
        addToast({
          title: "Erro ao fazer o upload!",
          type: "error",
          description: err?.message,
        });
      }
      setUploadingTemplates(false);
    } else {
      addToast({
        title: "Erro ao fazer o upload!",
        type: "error",
        description: "Ocorreu um erro. Tente novamente mais tarde",
      });
    }
  }

  async function handleRemoveTemplate() {
    setRemoveTemplate((prevData) => ({
      ...prevData,
      openModal: true,
      isRemoving: true,
    }));
    try {
      const response = await api.put(
        `/api/adm/contracts/templates/${removeTemplate.templateId}`,
        {
          filename: removeTemplate.filename,
        }
      );
      if (response.data.success) {
        addToast({ title: "Modelo apagado com sucesso!", type: "success" });
        if (response.data.success)
          setTemplates((prevTemplates) =>
            prevTemplates.filter(
              (template) => template.id !== response.data.data
            )
          );
      }
    } catch (err: any) {
      addToast({
        title: "Erro ao apagar o modelo",
        type: "error",
        description: err?.message,
      });
    }
    setRemoveTemplate((prevData) => ({
      ...prevData,
      openModal: false,
      isRemoving: false,
    }));
  }

  function handleCloseRemoveTemplateModal() {
    setRemoveTemplate((prevData) => ({ ...prevData, openModal: false }));
  }

  function handleOpenRemoveTemplateModel(id: number, filename: string) {
    setRemoveTemplate((prevData) => ({
      ...prevData,
      openModal: true,
      templateId: id,
      filename,
    }));
  }

  return (
    <Container>
      {!!loadApiError && (
        <ErrorMessageContainer>
          <h2>{loadApiError}</h2>
        </ErrorMessageContainer>
      )}
      {!loadApiError && (
        <>
          {removeTemplate.openModal && (
            <ModalRemoveTemplate
              open={removeTemplate.openModal}
              onClose={handleCloseRemoveTemplateModal}
              removingTemplate={removeTemplate.isRemoving}
              onRemoveTemplate={handleRemoveTemplate}
            />
          )}
          <Header />
          <h2>Modelos de Contrato</h2>
          {loadingTemplates && <TemplatesTableSkeleton />}
          {!loadingTemplates && templates.length === 0 && <TemplatesFallback />}
          {!loadingTemplates && templates.length !== 0 && (
            <TemplatesTable>
              {templates.map(({ id, filename }) => (
                <TemplateCard
                  key={id}
                  extension={extractFileExtension(filename)}
                  filename={filename}
                  id={id}
                  onOpenRemoveTemplateModal={() =>
                    handleOpenRemoveTemplateModel(id, filename)
                  }
                />
              ))}
            </TemplatesTable>
          )}
          <AttachFileButton>
            <label
              htmlFor="add-file-btn"
              id={uploadingTemplates ? "disabled-file-btn" : "add-file-lbl"}
            >
              <MdOutlineAttachment size={24} fontWeight={600} />{" "}
              {uploadingTemplates
                ? "Adicionando modelo..."
                : "Adicionar modelo"}
            </label>
            <input
              disabled={uploadingTemplates}
              type="file"
              id="add-file-btn"
              accept=".docx, .pptx"
              onChange={onFileUpload}
            />
          </AttachFileButton>
          <Footer />
        </>
      )}
    </Container>
  );
};

export default ShowContractTemplates;
